import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'

import { useQuery } from '@tanstack/react-query'

import type AccessLevel from '@/SDK/AccessLevel'
import { PCInstance } from '@/SDK/index'

interface PermissionCheckerProps {
  permissionName: string
  minAccessLevel: AccessLevel
  children: ReactNode | ((hasPermission: boolean) => ReactNode)
  onPermissionGranted?: (minAccessLevel: AccessLevel) => void
}

export default function PermissionCheckWrapper({
  children,
  permissionName,
  minAccessLevel,
  onPermissionGranted
}: PermissionCheckerProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['permissionCheck', permissionName, minAccessLevel],
    queryFn: () => PCInstance.hasPermission(permissionName, minAccessLevel)
  })

  const hasFiredOnPermissionGranted = useRef(false)

  useEffect(() => {
    if (data?.hasPermission && !hasFiredOnPermissionGranted.current) {
      onPermissionGranted?.(minAccessLevel)
      hasFiredOnPermissionGranted.current = true
    }
  }, [data?.hasPermission, onPermissionGranted, minAccessLevel])

  if (!(children instanceof Function) && (isLoading || (!isLoading && !data?.hasPermission))) return null

  return children instanceof Function ? children(data?.hasPermission ?? false) : children
}
